import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { mediaApi, groesseLesbar, KIND_LABELS, MEDIA_ACCEPT, type MediaAsset } from '../../services/mediaService';
import { blobSpeichern } from '../../services/download';
import { MediaPreview } from './MediaPreview';
import { useMediaUpload, uploadStatusText } from './useMediaUpload';

/**
 * Kompakte Medienliste einer Beitragsidee (PostIdeasPage): zeigt die
 * verknuepften Medien, laedt direkt zur Idee hoch und bietet Download und
 * Vorschau. Pflege (Titel, Thema, Loeschen) bleibt in der Medienbibliothek.
 */
export const IdeaMediaPanel: React.FC<{ postIdeaId: string; topic?: string; darfBearbeiten: boolean }> = ({
  postIdeaId, topic, darfBearbeiten,
}) => {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState('');
  const [vorschau, setVorschau] = useState<MediaAsset | null>(null);
  const dateiFeld = useRef<HTMLInputElement>(null);

  const laden = useCallback(async () => {
    try {
      setLoading(true);
      setAssets(await mediaApi.list({ postIdeaId }));
    } catch {
      setFehler('Die Medien konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [postIdeaId]);

  useEffect(() => { laden(); }, [laden]);

  const { zeilen, hochladen } = useMediaUpload(laden);

  const herunterladen = async (a: MediaAsset) => {
    try {
      blobSpeichern(await mediaApi.content(a.id, true), a.originalName);
    } catch {
      setFehler('Der Download ist fehlgeschlagen.');
    }
  };

  const laufend = zeilen.filter((z) => z.status === 'laeuft' || z.status === 'fehler');

  return (
    <div className="text-sm" aria-label="Medien der Beitragsidee" role="region">
      {fehler && <div className="alert alert-error">{fehler}</div>}
      {loading && assets.length === 0 ? (
        <span className="text-muted">Medien werden geladen …</span>
      ) : assets.length === 0 ? (
        <span className="text-muted">Noch keine Medien an dieser Idee.</span>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {assets.map((a) => (
            <li key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
              <span className="badge">{KIND_LABELS[a.kind] ?? a.kind}</span>
              <span>{a.title || a.originalName}</span>
              <span className="text-muted">{groesseLesbar(a.sizeBytes)}</span>
              {a.kind !== 'document' && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setVorschau(a)}>Vorschau</button>
              )}
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => herunterladen(a)}>Herunterladen</button>
            </li>
          ))}
        </ul>
      )}
      {laufend.map((z) => (
        <div key={z.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>{z.name}</span>
          <progress max={1} value={z.anteil} aria-label={`Fortschritt ${z.name}`} />
          <span className={z.status === 'fehler' ? 'text-danger' : 'text-muted'}>{uploadStatusText(z)}</span>
        </div>
      ))}
      <div className="mt-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {darfBearbeiten && (
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => dateiFeld.current?.click()}>
              Datei hochladen
            </button>
            <input
              ref={dateiFeld}
              type="file"
              multiple
              accept={MEDIA_ACCEPT}
              style={{ display: 'none' }}
              aria-label="Datei zur Beitragsidee hochladen"
              onChange={(e) => {
                const liste = e.target.files;
                if (liste && liste.length > 0) hochladen(Array.from(liste), { postIdeaId, topic: topic || undefined });
                e.target.value = '';
              }}
            />
          </>
        )}
        <Link to="/media">Zur Medienbibliothek</Link>
      </div>
      {vorschau && <MediaPreview asset={vorschau} onClose={() => setVorschau(null)} darfBearbeiten={darfBearbeiten} />}
    </div>
  );
};

export default IdeaMediaPanel;
